import React, {FormEvent, useCallback, useEffect, useState} from "react";
import {useNavigate} from "react-router-dom";
import {
    CheckboxText,
    CheckboxWrapper,
    Header,
    RequiredFieldsNote,
    StyledCard,
    StyledForm,
    StyledTextField,
    Subtext
} from "./RegisterAdmin.styled";
import {Button, Checkbox, CircularProgress, IconButton, InputAdornment, Link} from "@mui/material";
import {brand} from "../../../theme/brand";
import {Visibility, VisibilityOff} from "@mui/icons-material";
import {AuthContainer} from "../Auth.styled";
import {usePageTitle} from '../../../hooks/usePageTitle';
import LogoWithLanguageSwitcher from '../../../components/common/LogoWithLanguageSwitcher';
import {PageTitleKeys} from '../../../config/routeConstants';
import {parseError} from "../../../errors/http.service";
import {useTranslation} from 'react-i18next';
import {validatePassword, validatePasswordMatch} from "../../../utils/password-validation";
import {useSnackbar} from "../../../contexts/SnackbarContext";
import {useContext} from "../../../contexts/Context";
import {getEmailValidationError} from "../../../utils/email-validation";
import {authService, RegisterAdminParamsType} from "../../../services/auth.service";
import EulaDialog from "../EulaDialog";
import {EulaAgreementResponse, termsService} from "../../../services/terms.service";
import axios from "axios";

async function registerAdmin({email, password, confirmPassword, eulaAccepted, eulaVersion}: RegisterAdminParamsType) {
    try {
        await authService.registerAdmin({email, password, confirmPassword, eulaAccepted, eulaVersion});
        return true;
    } catch (error) {
        console.error("Registration error:", error);
        return false;
    }
}

const RegisterAdmin = () => {
    usePageTitle(PageTitleKeys.Register);

    const {t} = useTranslation();
    const navigate = useNavigate();
    const {showSnackbar} = useSnackbar();

    const {setIsAdminExists} = useContext();

    const [email, setEmail] = useState<string>("");
    const [emailTouched, setEmailTouched] = useState<boolean>(false);
    const [password, setPassword] = useState<string>("");
    const [confirmPassword, setConfirmPassword] = useState<string>("");
    const [passwordTouched, setPasswordTouched] = useState<boolean>(false);
    const [confirmPasswordTouched, setConfirmPasswordTouched] = useState<boolean>(false);

    const [consent, setConsent] = useState<boolean>(false);

    const [isFetchingToken, setIsFetchingToken] = useState<boolean>(false);
    const [showPassword, setShowPassword] = useState<boolean>(false);

    const [submitted, setSubmitted] = useState<boolean>(false);

    const emailValidationError = getEmailValidationError(email, true);
    const passwordValidationError = validatePassword(password);
    const confirmPasswordValidationError = validatePasswordMatch(password, confirmPassword) ?? validatePassword(confirmPassword);
    const showEmailError = (emailTouched || submitted) && !!emailValidationError;
    const showPasswordError = (passwordTouched || submitted) && !!passwordValidationError;
    const showConfirmPasswordError = (confirmPasswordTouched || submitted) && !!confirmPasswordValidationError;

    const [eulaData, setEulaData] = useState<EulaAgreementResponse | null>(null);
    const [isEulaLoading, setIsEulaLoading] = useState<boolean>(true);
    const [eulaAccepted, setEulaAccepted] = useState<boolean>(false);
    const [isEulaDialogOpen, setIsEulaDialogOpen] = useState<boolean>(false);

    const fetchEulaContent = async () => {
        try {
            setIsEulaLoading(true);
            const data = await termsService.fetchEulaAgreement();
            setEulaData(data);
        } catch (e) {
            if (axios.isAxiosError(e) && !e.response) {
                return;
            }

            console.error(e);
            showSnackbar(t('register.fetchEulaError'), "error");
        } finally {
            setIsEulaLoading(false);
        }
    };

    useEffect(() => {
        void fetchEulaContent();
    }, []);

    const handleRegister = useCallback(async () => {
        setSubmitted(true);

        if (!consent
            || (!!eulaData && !eulaAccepted)
            || passwordValidationError
            || confirmPasswordValidationError
            || emailValidationError
        ) {
            setSubmitted(false);
            return;
        }

        setIsFetchingToken(true);
        try {
            const passed = await registerAdmin({email, password, confirmPassword, eulaAccepted, eulaVersion: eulaData?.version ?? ''});

            if (passed) {
                setIsAdminExists(true);

                showSnackbar(t('register.successMessage'), 'success');
                navigate("/login");
            } else {
                showSnackbar(t('register.errorMessage'), 'error');
            }
        } catch (error) {
            console.error('Error during registration:', error);
            showSnackbar(parseError(error), "error");
        } finally {
            setIsFetchingToken(false);
            setSubmitted(false)
        }
    }, [consent, eulaData, eulaAccepted, passwordValidationError, confirmPasswordValidationError, emailValidationError, email, password, confirmPassword, setIsAdminExists, showSnackbar, t, navigate]);

    return (
        <AuthContainer role="region" aria-labelledby="register-title">
            <LogoWithLanguageSwitcher src={brand.logo} alt={t('auth.logo.alt')} clickable={false}/>

            <StyledCard>
                <Header id="register-title">{t('register.admin.title')}</Header>
                <Subtext>{t('register.admin.description')}</Subtext>

                <StyledForm autoComplete="off"
                            aria-live="polite"
                            aria-atomic={true}
                            aria-busy={isFetchingToken}
                            onSubmit={async (e: FormEvent<HTMLFormElement>) => {
                                e.preventDefault();
                                await handleRegister();
                            }}
                >
                    <div>
                        <StyledTextField
                            label={t('register.email')}
                            value={email}
                            onChange={(e) => {
                                setEmail(e.target.value);
                            }}
                            onBlur={() => setEmailTouched(true)}
                            error={showEmailError}
                            helperText={showEmailError && emailValidationError ? String(t(emailValidationError.key)) : ""}
                            fullWidth
                            disabled={isFetchingToken}
                            inputProps={{'aria-label': t('register.email.ariaLabel') as string}}
                        />
                    </div>

                    <div>
                        <StyledTextField
                            label={t('register.password')}
                            type={showPassword ? "text" : "password"}
                            autoComplete="new-password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            onBlur={() => setPasswordTouched(true)}
                            error={showPasswordError}
                            helperText={showPasswordError ? String(t(passwordValidationError!.key)) : ""}
                            fullWidth
                            disabled={isFetchingToken}
                            inputProps={{'aria-label': t('register.password.ariaLabel') as string}}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={() => setShowPassword((prev) => !prev)}
                                            edge="end"
                                            sx={{color: 'primary.main'}}
                                            disabled={isFetchingToken}
                                            aria-label={t('register.password.toggleVisibility') as string}
                                        >
                                            {showPassword ? <VisibilityOff/> : <Visibility/>}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </div>

                    <div>
                        <StyledTextField
                            label={t('register.confirmPassword')}
                            type={showPassword ? "text" : "password"}
                            autoComplete="new-password"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            onBlur={() => setConfirmPasswordTouched(true)}
                            error={showConfirmPasswordError}
                            helperText={showConfirmPasswordError && confirmPasswordValidationError ? String(t(confirmPasswordValidationError.key)) : ""}
                            fullWidth
                            disabled={isFetchingToken}
                            inputProps={{'aria-label': t('register.password.ariaLabel') as string}}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={() => setShowPassword((prev) => !prev)}
                                            edge="end"
                                            sx={{color: 'primary.main'}}
                                            disabled={isFetchingToken}
                                            aria-label={t('register.password.toggleVisibility') as string}
                                        >
                                            {showPassword ? <VisibilityOff/> : <Visibility/>}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <div style={{height: '24px', marginTop: '4px'}}></div>
                    </div>

                    <RequiredFieldsNote>{t('register.requiredFieldsInfo')}</RequiredFieldsNote>

                    <CheckboxWrapper>
                        <Checkbox
                            checked={consent}
                            onChange={(e) => setConsent(e.target.checked)}
                            sx={{padding: "0 8px 0 0"}}
                            disabled={isFetchingToken || isEulaLoading}
                            inputProps={{'aria-label': t('register.consent.ariaLabel') as string}}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setConsent(prev => !prev);
                                }
                            }}
                        />
                        <CheckboxText>
                            {t('register.consent')}
                        </CheckboxText>
                    </CheckboxWrapper>

                    {eulaData && (
                        <CheckboxWrapper as="div">
                            <Checkbox
                                required
                                checked={eulaAccepted}
                                onChange={(e) => setEulaAccepted(e.target.checked)}
                                sx={{padding: "0 8px 0 0"}}
                                disabled={isFetchingToken || isEulaLoading}
                                inputProps={{'aria-label': t('register.eulaAcceptance.ariaLabel') as string}}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        setEulaAccepted(prev => !prev);
                                    }
                                }}
                            />
                            <CheckboxText>
                                {t('register.eulaAcceptance')}
                                <Link
                                    component="span"
                                    role="button"
                                    tabIndex={0}
                                    sx={{ cursor: "pointer" }}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setIsEulaDialogOpen(true);
                                    }}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            setIsEulaDialogOpen(true);
                                        }
                                    }}
                                >
                                    {t('register.eulaAcceptanceLink')}
                                </Link>
                                {' *'}
                            </CheckboxText>
                        </CheckboxWrapper>
                    )}

                    {(isFetchingToken || isEulaLoading) && (
                        <div role="status" aria-live="polite" style={{
                            position: 'absolute',
                            width: 1,
                            height: 1,
                            overflow: 'hidden',
                            clip: 'rect(1px, 1px, 1px, 1px)'
                        }}>
                            {t('register.loading')}
                        </div>
                    )}

                    <Button type="submit"
                            variant="contained"
                            color="primary"
                            fullWidth
                            disabled={
                                isFetchingToken
                                || isEulaLoading
                                || (!!eulaData && isEulaDialogOpen)
                                || (!!eulaData && !eulaAccepted)
                                || !consent
                                || !email
                                || !password
                                || !confirmPassword
                                || showPasswordError
                                || showConfirmPasswordError
                                || showEmailError
                            }
                    >
                        {(isFetchingToken || isEulaLoading)
                            ? <CircularProgress size={24} color="primary" />
                            : t('register.registerButton')
                        }
                    </Button>
                </StyledForm>
            </StyledCard>

            {(eulaData && isEulaDialogOpen) && (
                <EulaDialog open={isEulaDialogOpen} onClose={() => setIsEulaDialogOpen(false)} eulaData={eulaData} />
            )}
        </AuthContainer>
    );
};

export default RegisterAdmin;
